import { z } from 'zod';

export const AiGenerationKind = {
  CAMPAIGN: 'CAMPAIGN',
  AD_COPY: 'AD_COPY',
  SOCIAL_CAPTION: 'SOCIAL_CAPTION',
  CONTENT_TOPICS: 'CONTENT_TOPICS',
  ARTICLE: 'ARTICLE',
  IMAGE_PROMPT: 'IMAGE_PROMPT',
  IMAGE: 'IMAGE',
  RECOMMENDATION: 'RECOMMENDATION',
  REPORT_COMMENTARY: 'REPORT_COMMENTARY',
} as const;
export type AiGenerationKind = (typeof AiGenerationKind)[keyof typeof AiGenerationKind];

export const AiGenerationStatus = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  ERROR: 'ERROR',
} as const;
export type AiGenerationStatus = (typeof AiGenerationStatus)[keyof typeof AiGenerationStatus];

export const promptVersionSchema = z.object({
  kind: z.nativeEnum(AiGenerationKind),
  version: z.number().int().min(1),
  systemPrompt: z.string().min(10).max(30000),
  userTemplate: z.string().min(10).max(30000),
  model: z.string().max(80),
  temperature: z.number().min(0).max(2).default(0.7),
  maxOutputTokens: z.number().int().min(256).max(32000).default(8000),
  isActive: z.boolean().default(false),
  notes: z.string().max(2000).optional(),
});
export type PromptVersionInput = z.infer<typeof promptVersionSchema>;

export interface AiUsage {
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costCents: number;
}

/**
 * Preço por 1M de tokens em centavos de USD. Ajustável por variável de
 * ambiente; usado apenas para estimar custo por cliente.
 */
export const DEFAULT_MODEL_PRICING: Record<string, { inputPer1M: number; outputPer1M: number }> = {
  'gpt-4.1': { inputPer1M: 200, outputPer1M: 800 },
  'gpt-4.1-mini': { inputPer1M: 40, outputPer1M: 160 },
  'gpt-4o': { inputPer1M: 250, outputPer1M: 1000 },
  'gpt-4o-mini': { inputPer1M: 15, outputPer1M: 60 },
};

export function estimateCostCents(
  model: string,
  inputTokens: number,
  outputTokens: number,
  pricing: Record<string, { inputPer1M: number; outputPer1M: number }> = DEFAULT_MODEL_PRICING,
): number {
  const price = pricing[model] ?? pricing['gpt-4.1-mini'];
  const cost = (inputTokens / 1_000_000) * price.inputPer1M + (outputTokens / 1_000_000) * price.outputPer1M;
  return Math.round(cost * 100) / 100;
}

export const aiCostQuerySchema = z.object({
  clientId: z.string().uuid().optional(),
  from: z.coerce.date(),
  to: z.coerce.date(),
  groupBy: z.enum(['CLIENT', 'KIND', 'MODEL', 'DAY']).default('CLIENT'),
});
export type AiCostQuery = z.infer<typeof aiCostQuerySchema>;
